import { useCallback, useState } from "react";

/**
 * Client-side source of truth for the "accept small overlaps" tolerance
 * (minutes). Ephemeral by design (see the design doc): persisted in
 * localStorage so it sticks across reloads for the operator, and passed as a
 * parameter to the issues list + force-place calls. The server has its own
 * `Scheduler:AllowedOverlapMinutes` default; this only drives the FE control.
 */
const STORAGE_KEY = "bt-overlap-tolerance-minutes";
export const DEFAULT_OVERLAP_TOLERANCE = 5;
export const MAX_OVERLAP_TOLERANCE = 120;

function clampTolerance(value: number): number {
  if (Number.isNaN(value)) return 0;
  return Math.min(Math.max(Math.trunc(value), 0), MAX_OVERLAP_TOLERANCE);
}

function readStored(): number {
  if (typeof window === "undefined") return DEFAULT_OVERLAP_TOLERANCE;
  const raw = window.localStorage.getItem(STORAGE_KEY);
  if (raw === null) return DEFAULT_OVERLAP_TOLERANCE;
  const parsed = Number.parseInt(raw, 10);
  return Number.isNaN(parsed) ? DEFAULT_OVERLAP_TOLERANCE : clampTolerance(parsed);
}

export function useOverlapTolerance() {
  const [tolerance, setToleranceState] = useState<number>(readStored);

  const setTolerance = useCallback((value: number) => {
    const clamped = clampTolerance(value);
    setToleranceState(clamped);
    if (typeof window !== "undefined") {
      window.localStorage.setItem(STORAGE_KEY, String(clamped));
    }
  }, []);

  return { tolerance, setTolerance } as const;
}
